import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CalendarPlus, MapPin, Sparkles, Trash2, X } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { api } from "../lib/api";
import type { ItineraryDay, ItineraryItem, PlaceSuggestion } from "../lib/types";
import { PlaceCombobox } from "./PlaceCombobox";
import { TimeDurationRoller } from "./TimeDurationRoller";

const activityTypes = [
  ["attraction", "Attraction"],
  ["restaurant", "Restaurant or meal"],
  ["cafe", "Cafe or snack"],
  ["train", "Train or transit"],
  ["shopping", "Shopping"],
  ["rest", "Rest or free time"],
  ["other", "Other"],
] as const;

type ActivityForm = {
  title: string;
  item_type: string;
  start_time: string;
  end_time: string;
  description: string;
  cost: string;
  must_do: boolean;
  optional: boolean;
  booking_required: boolean;
};

function placeFromItem(item?: ItineraryItem): PlaceSuggestion | null {
  if (!item?.latitude || !item.longitude || !item.place_name) return null;
  return {
    provider_id: `saved:${item.id}`,
    name: item.place_name,
    display_name: item.address || item.place_name,
    latitude: item.latitude,
    longitude: item.longitude,
    timezone: null,
    country_code: null,
    country: null,
    admin1: null,
    locality: null,
    feature_code: item.item_type,
    category: item.item_type,
    suggestion_kind: "Saved place",
    distance_m: null,
    source_url: null,
  };
}

function initialForm(item?: ItineraryItem, suggestedStartTime = "", suggestedEndTime = ""): ActivityForm {
  return {
    title: item?.title || "",
    item_type: item?.item_type || "attraction",
    start_time: item?.start_time?.slice(0, 5) || suggestedStartTime,
    end_time: item?.end_time?.slice(0, 5) || suggestedEndTime,
    description: item?.description || "",
    cost: item?.cost || "",
    must_do: item?.must_do || false,
    optional: item?.optional || false,
    booking_required: item?.booking_required || false,
  };
}

export function ItineraryItemDialog({
  tripId,
  day,
  currency,
  anchor,
  item,
  suggestedStartTime = "",
  suggestedEndTime = "",
  suggestedPosition,
  insertionLabel,
  onClose,
}: {
  tripId: string;
  day: ItineraryDay;
  currency: string;
  anchor: { name: string; latitude: string | null; longitude: string | null };
  item?: ItineraryItem;
  suggestedStartTime?: string;
  suggestedEndTime?: string;
  suggestedPosition?: number;
  insertionLabel?: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ActivityForm>(() => initialForm(item, suggestedStartTime, suggestedEndTime));
  const [place, setPlace] = useState<PlaceSuggestion | null>(() => placeFromItem(item));
  const [formError, setFormError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const editing = Boolean(item);
  const overlappingItems = form.start_time && form.end_time
    ? day.items.filter((candidate) => candidate.id !== item?.id && candidate.start_time && candidate.end_time && form.start_time < candidate.end_time.slice(0, 5) && form.end_time > candidate.start_time.slice(0, 5))
    : [];
  const { data: nearby = [], isFetching: loadingNearby } = useQuery({
    queryKey: ["nearby-highlights", anchor.latitude, anchor.longitude],
    queryFn: () => api<PlaceSuggestion[]>(`/places/nearby?lat=${anchor.latitude}&lon=${anchor.longitude}&limit=8`),
    enabled: Boolean(anchor.latitude && anchor.longitude),
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });
  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api<ItineraryItem>(
      item
        ? `/trips/${tripId}/itinerary/items/${item.id}`
        : `/trips/${tripId}/itinerary/days/${day.id}/items`,
      { method: item ? "PATCH" : "POST", body: JSON.stringify(payload) },
    ),
    onSuccess: () => {
      onClose();
      void queryClient.invalidateQueries({ queryKey: ["trip-dashboard", tripId] });
    },
  });
  const deleteMutation = useMutation({
    mutationFn: () => api<void>(`/trips/${tripId}/itinerary/items/${item!.id}`, { method: "DELETE" }),
    onSuccess: () => {
      onClose();
      void queryClient.invalidateQueries({ queryKey: ["trip-dashboard", tripId] });
    },
  });

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeWithKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !mutation.isPending && !deleteMutation.isPending) onClose();
    };
    document.addEventListener("keydown", closeWithKeyboard);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeWithKeyboard);
    };
  }, [deleteMutation.isPending, mutation.isPending, onClose]);

  const update = <Key extends keyof ActivityForm>(key: Key, value: ActivityForm[Key]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setFormError("");
  };
  const selectPlace = (nextPlace: PlaceSuggestion | null, fromHighlight = false) => {
    setPlace(nextPlace);
    setFormError("");
    if (nextPlace && fromHighlight && !editing) {
      setForm((current) => ({ ...current, title: nextPlace.name, item_type: nextPlace.category === "cafe" ? "cafe" : "attraction", must_do: nextPlace.suggestion_kind === "Must visit" }));
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (form.title.trim().length < 2) return setFormError("Give the activity a name of at least two characters.");
    if (form.start_time && form.end_time && form.end_time <= form.start_time) return setFormError("End time must be after the start time.");
    if (form.item_type !== "rest" && !place) return setFormError(`Choose an actual mapped place in or around ${anchor.name}.`);
    mutation.mutate({
      title: form.title.trim(),
      item_type: form.item_type,
      place_name: place?.name || null,
      address: place?.display_name || null,
      latitude: place?.latitude || null,
      longitude: place?.longitude || null,
      start_time: form.start_time || null,
      end_time: form.end_time || null,
      description: form.description.trim() || null,
      cost: form.cost ? Number(form.cost) : null,
      must_do: form.must_do,
      optional: form.optional,
      booking_required: form.booking_required,
      ...(!editing && suggestedPosition !== undefined ? { position: suggestedPosition } : {}),
    });
  };

  const dateLabel = day.date
    ? new Date(`${day.date}T00:00:00`).toLocaleDateString("en", { weekday: "long", day: "numeric", month: "long" })
    : "Date still flexible";

  return <div className="modal-backdrop activity-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !mutation.isPending && onClose()}>
    <section className="activity-dialog" role="dialog" aria-modal="true" aria-labelledby="activity-dialog-title">
      <header>
        <div className="activity-dialog-icon"><CalendarPlus size={20} /></div>
        <div><p className="eyebrow">DAY {day.day_number} · BASED AT {anchor.name}</p><h2 id="activity-dialog-title">{editing ? `Edit ${item?.title}` : insertionLabel || "Add an activity"}</h2><span>{dateLabel}</span></div>
        <button onClick={onClose} disabled={mutation.isPending} aria-label="Close activity form"><X size={19} /></button>
      </header>
      <form onSubmit={submit}>
        <div className="activity-form-grid">
          <label className="activity-title-field">Activity name<input autoFocus value={form.title} onChange={(event) => update("title", event.target.value)} placeholder="Morning at the botanical garden" maxLength={180} /></label>
          <label>Type<select value={form.item_type} onChange={(event) => update("item_type", event.target.value)}>{activityTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <div className="activity-place-field field-label"><span>Actual place or meeting point</span><PlaceCombobox value={place} initialText={item?.address || item?.place_name || ""} context={anchor.name} latitude={anchor.latitude} longitude={anchor.longitude} onSelect={selectPlace} /></div>
          <div className="activity-time-fields"><TimeDurationRoller startTime={form.start_time} endTime={form.end_time} label="Start time" durationLabelText="Duration" onChange={(startTime, endTime) => { setForm((current) => ({ ...current, start_time: startTime, end_time: endTime })); setFormError(""); }} /></div>
          <label className="activity-notes-field">Notes<textarea rows={3} value={form.description} onChange={(event) => update("description", event.target.value)} placeholder="Tickets, meeting instructions, or why it belongs in the day." maxLength={5000} /></label>
          <label>Estimated cost ({currency})<input type="number" min="0" step="0.01" value={form.cost} onChange={(event) => update("cost", event.target.value)} placeholder="Optional" /></label>
        </div>
        {overlappingItems.length > 0 && <section className="schedule-alert schedule-shift-note" role="status"><Sparkles size={18} /><div><strong>The day will make room automatically</strong><p>Saving this activity will push {overlappingItems.map((candidate) => candidate.title).join(", ")} and any following moments later while keeping their durations.</p></div></section>}
        <section className="nearby-suggestions" aria-label={`Suggestions around ${anchor.name}`}>
          <div><span><Sparkles size={15} />Suggestions around {anchor.name}</span><small>Must-visit, famous, and special places are selected from mapped nearby landmarks.</small></div>
          <div className="nearby-suggestion-list">
            {nearby.map((suggestion) => <button type="button" key={suggestion.provider_id} onClick={() => selectPlace(suggestion, true)}><span className="nearby-photo">{suggestion.image_url ? <img src={suggestion.image_url} alt={`View of ${suggestion.name}`} loading="lazy" /> : <MapPin size={17} />}</span><span><strong>{suggestion.name}</strong><small>{suggestion.suggestion_kind}{suggestion.distance_m !== null ? ` · ${(suggestion.distance_m / 1000).toFixed(1)} km` : ""}</small></span></button>)}
            {loadingNearby && !nearby.length && <span className="suggestions-loading">Finding places worth your time…</span>}
            {!loadingNearby && !nearby.length && <span className="suggestions-loading">Search above to choose an exact mapped place.</span>}
          </div>
        </section>
        <fieldset className="activity-flags"><legend>Planning signals</legend><label><input type="checkbox" checked={form.must_do} onChange={(event) => setForm((current) => ({ ...current, must_do: event.target.checked, optional: event.target.checked ? false : current.optional }))} /><span><strong>Must do</strong><small>Keep this activity prominent</small></span></label><label><input type="checkbox" checked={form.booking_required} onChange={(event) => update("booking_required", event.target.checked)} /><span><strong>Booking needed</strong><small>Flag it for advance action</small></span></label><label><input type="checkbox" checked={form.optional} onChange={(event) => setForm((current) => ({ ...current, optional: event.target.checked, must_do: event.target.checked ? false : current.must_do }))} /><span><strong>Optional</strong><small>Easy to skip if the day changes</small></span></label></fieldset>
        {confirmDelete && <section className="removal-alert" role="alert"><AlertTriangle size={18} /><div><strong>Remove {item?.title}?</strong><p>The planner will leave its time as breathing room and highlight any remaining timing conflicts.</p><div><button type="button" onClick={() => setConfirmDelete(false)}>Keep activity</button><button type="button" className="danger-text" disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>{deleteMutation.isPending ? "Removing…" : "Remove and keep free time"}</button></div></div></section>}
        {(formError || mutation.error || deleteMutation.error) && <p className="form-error" role="alert">{formError || mutation.error?.message || deleteMutation.error?.message}</p>}
        <footer>{editing && <button type="button" className="activity-delete" onClick={() => setConfirmDelete(true)} disabled={mutation.isPending || deleteMutation.isPending}><Trash2 size={15} />Remove</button>}<span className="activity-footer-spacer" /><button type="button" className="button ghost" onClick={onClose} disabled={mutation.isPending || deleteMutation.isPending}>Cancel</button><button type="submit" className="button primary" disabled={mutation.isPending || deleteMutation.isPending}>{mutation.isPending ? "Saving the moment…" : editing ? "Save activity" : "Add to itinerary"}<Sparkles size={16} /></button></footer>
      </form>
    </section>
  </div>;
}
